import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// Import actions
import { parentalControlSuccess } from 'store/actions/tv/parental_control/actions';

// Import types
import type { RootState } from 'store/store';
import type { VerificationState } from 'types/api/subscriber';

// Import helpers
import { getUserToken } from 'helpers/auth';

// Import thunk
import { fetchSubscriberDetails, subscriberLogout, fetchSubscriberProducts, fetchSubscriberPackets, fetchSubscriberInLocal } from './thunk';

// Import state
import { initialState } from './state';

const subscriberSlice = createSlice({
	name: 'subscriber',
	initialState,
	reducers: {
		hideUnavailableToggle: (state, action) => {
			if (state.details) {
				state.details.hide_unavailable_products = action.payload;
			}
		},
		setVerificationState: (state, action: PayloadAction<VerificationState>) => {
			if (state.details) {
				state.details.identity_verification_state = action.payload;
			}
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchSubscriberDetails.pending, (state) => {
			state.isLoading = true;
			state.isError = false;
		});
		builder.addCase(fetchSubscriberDetails.fulfilled, (state, action) => {
			state.details = action.payload;
			state.token = getUserToken();
			state.isLoading = false;
			state.isError = false;
		});
		builder.addCase(fetchSubscriberDetails.rejected, (state) => {
			state.details = null;
			state.isLoading = false;
			state.isError = true;
			state.token = null;
		});
		builder.addCase(subscriberLogout.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(subscriberLogout.fulfilled, (state) => {
			state.details = null;
			state.isLoading = false;
			state.token = null;
		});
		builder.addCase(subscriberLogout.rejected, (state) => {
			state.isLoading = false;
		});
		builder.addCase(fetchSubscriberProducts.pending, (state) => {
			state.products.isLoading = true;
			state.products.isError = false;
		});
		builder.addCase(fetchSubscriberProducts.fulfilled, (state, action) => {
			state.products.data = action.payload;
			state.products.isLoading = false;
		});
		builder.addCase(fetchSubscriberProducts.rejected, (state) => {
			state.products.isLoading = false;
			state.products.isError = true;
		});
		builder.addCase(fetchSubscriberPackets.pending, (state) => {
			state.packets.isLoading = true;
			state.packets.isError = false;
			state.products.isLoading = true;
			state.products.isError = false;
		});
		builder.addCase(fetchSubscriberPackets.fulfilled, (state, action) => {
			state.packets.data = action.payload.packets;
			state.packets.isLoading = false;
			state.products.data = action.payload.products;
			state.products.isLoading = false;
		});
		builder.addCase(fetchSubscriberPackets.rejected, (state) => {
			state.packets.isLoading = false;
			state.packets.isError = true;
			state.products.isLoading = false;
			state.products.isError = true;
		});
		builder.addCase(parentalControlSuccess, (state, action) => {
			state.token = action.payload.token;
			if (state.details) {
				state.details.parental_control_rating = action.payload.rating;
			}
		});
		builder.addCase(fetchSubscriberInLocal.fulfilled, (state, action) => {
			if (state.details) {
				state.details.subscriber_local_active = action.payload;
			}
		});
	}
});

export const selectSubscriber = (state: RootState) => state.subscriber;

export const { hideUnavailableToggle, setVerificationState } = subscriberSlice.actions;

export default subscriberSlice.reducer;
